import { Box, Chip, Divider, IconButton, Stack, Typography } from "@mui/material";
import {
  ChildCareOutlined as ChildCareOutlinedIcon,
  ChevronLeftRounded as ChevronLeftRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  GroupOutlined as GroupOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BudgetRange } from "../../../__generated__/types";
import { BUDGET_RANGES } from "../../../components/homeowner-preferences-quiz/constants";
import { PROJECT_SCOPE_OPTIONS } from "../../homeowner-bio-page/data";
import { bugetRangeLabels } from "../../../utils/option-helpers";
import { CHIP_COLOR } from "../constants";
import { EXPERIENCE_LABELS, EXPERIENCE_OPTIONS } from "../data";
import type { ExperienceRating, HomeownerProjectRecord } from "../types";
import { getPetImageSrc } from "../../../utils/pet-images";
import {
  formatBudgetRangeLabel,
  formatCountLabel,
  formatKidsAges,
  formatShortDate,
  formatValue,
  formatYesNo,
  toTitleCase,
} from "../utils";

type PetDisplayItem = {
  label: string;
  iconSrc?: string | null;
};

type PreferenceTabProps = {
  projects: HomeownerProjectRecord[];
};

const getOptionLabel = (options: { value: string; label: string }[], value?: string | null) => {
  if (!value) return "";
  const match = options.find((option) => option.value === value || option.label === value);
  return match?.label ?? toTitleCase(value);
};

const getMultiOptionLabel = (
  options: { value: string; label: string }[],
  values?: Array<string | null | undefined>
) => {
  if (!values?.length) return "";
  const labels = values
    .filter(Boolean)
    .map((value) => getOptionLabel(options, String(value)))
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "";
};

const PreferenceTab = ({ projects }: PreferenceTabProps) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const projectScrollRef = useRef<HTMLDivElement | null>(null);

  const isProjectCompleted = (project: HomeownerProjectRecord) => {
    const normalized = project.status?.trim().toLowerCase().replace(/\s+/g, "_");
    if (normalized) return normalized === "completed";
    return Boolean(project.completionConfirmedAt || project.archivedAt);
  };

  const isProjectActive = (project: HomeownerProjectRecord) => {
    const normalized = project.status?.trim().toLowerCase().replace(/\s+/g, "_");
    if (normalized) {
      return ["active", "in_progress", "paused"].includes(normalized);
    }
    if (isProjectCompleted(project)) return false;
    return Boolean(project.active);
  };

  const sortedProjects = useMemo(() => {
    const list = projects
      .filter(Boolean)
      .filter((project) => isProjectCompleted(project) || isProjectActive(project));
    return [...list].sort((a, b) => {
      const dateA = a.projectStartDate ? new Date(a.projectStartDate).getTime() : 0;
      const dateB = b.projectStartDate ? new Date(b.projectStartDate).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA;
      return (a.projectName ?? "").localeCompare(b.projectName ?? "");
    });
  }, [projects]);

  useEffect(() => {
    if (!sortedProjects.length) {
      setSelectedProjectId(null);
      return;
    }
    if (selectedProjectId && sortedProjects.some((project) => project._id === selectedProjectId)) {
      return;
    }
    const defaultProject =
      sortedProjects.find((project) => project.active) ?? sortedProjects[0];
    setSelectedProjectId(defaultProject?._id ?? null);
  }, [selectedProjectId, sortedProjects]);

  const selectedProject = useMemo(
    () =>
      sortedProjects.find((project) => project._id === selectedProjectId) ??
      sortedProjects[0] ??
      null,
    [selectedProjectId, sortedProjects]
  );

  const furniturePreferenceLabel = useMemo(() => {
    const values = (selectedProject?.furniturePreferences ?? []).filter(
      Boolean
    ) as string[];
    if (values.length) {
      return values.map((value) => toTitleCase(value)).join(", ");
    }
    return "Not provided";
  }, [selectedProject?.furniturePreferences]);

  const decisionMakersLabel = useMemo(() => {
    const value = selectedProject?.decisionMakers;
    return formatValue(value ? toTitleCase(value) : "", "Not provided");
  }, [selectedProject?.decisionMakers]);

  const workedWithDesignerValue = selectedProject?.workedWithDesigner;
  const experienceRating = useMemo(() => {
    if (!workedWithDesignerValue) return null;
    const rating = selectedProject?.experience?.rating;
    return rating === "sad" || rating === "neutral" || rating === "happy"
      ? rating
      : null;
  }, [selectedProject?.experience?.rating, workedWithDesignerValue]);

  const experienceNotes = useMemo(() => {
    if (!workedWithDesignerValue) return "";
    return selectedProject?.experience?.notes?.trim() ?? "";
  }, [selectedProject?.experience?.notes, workedWithDesignerValue]);

  const hobbiesList = useMemo(
    () => ((selectedProject?.hobbies ?? []).filter(Boolean) as string[]),
    [selectedProject?.hobbies]
  );

  const worksRemotelyLabel = useMemo(
    () => formatYesNo(selectedProject?.worksRemotely),
    [selectedProject?.worksRemotely]
  );

  const entertainRegularlyLabel = useMemo(
    () => formatYesNo(selectedProject?.entertainRegularly),
    [selectedProject?.entertainRegularly]
  );

  const petsList = useMemo<PetDisplayItem[]>(() => {
    const pets = (selectedProject?.pets ?? []) as Array<
      { type?: string | null } | string
    >;
    const typeMap = new Map<string, { label: string; raw: string }>();
    pets.forEach((pet) => {
      const rawType =
        (typeof pet === "string" ? pet : pet?.type)?.trim() ?? "";
      if (!rawType) return;
      const key = rawType.toLowerCase();
      if (!typeMap.has(key)) {
        typeMap.set(key, {
          label: toTitleCase(rawType) || rawType,
          raw: rawType,
        });
      }
    });
    if (!typeMap.size) {
      return [];
    }
    return Array.from(typeMap.values()).map((pet) => ({
      label: pet.label,
      iconSrc: getPetImageSrc(pet.raw),
    }));
  }, [selectedProject?.pets]);

  const adultsLabel = useMemo(
    () => formatCountLabel(selectedProject?.household?.adults, "Adult", "Adults"),
    [selectedProject?.household?.adults]
  );

  const kidsLabel = useMemo(
    () => formatCountLabel(selectedProject?.household?.kids, "Kid", "Kids"),
    [selectedProject?.household?.kids]
  );

  const kidsAgesLabel = useMemo(
    () => formatKidsAges(selectedProject?.household?.kidsAges),
    [selectedProject?.household?.kidsAges]
  );

  const getProjectBudgetLabel = (project: HomeownerProjectRecord) => {
    const budget = project.budgetRange;
    if (!budget) return "Not provided";
    const label =
      BUDGET_RANGES.find((option) => option.value === budget)?.label ??
      bugetRangeLabels[budget as BudgetRange] ??
      toTitleCase(budget);
    return label ? formatBudgetRangeLabel(label) : "Not provided";
  };

  const getProjectScopeLabel = (project: HomeownerProjectRecord) => {
    const scopeLabel = getMultiOptionLabel(
      PROJECT_SCOPE_OPTIONS,
      project.projectScope ?? undefined
    );
    return scopeLabel || "Not provided";
  };

  const scrollProjects = (direction: "left" | "right") => {
    const container = projectScrollRef.current;
    if (!container) return;
    const cardWidth =
      (container.firstElementChild as HTMLElement | null)?.offsetWidth ?? 240;
    const offset = direction === "left" ? -cardWidth - 16 : cardWidth + 16;
    container.scrollBy({ left: offset, behavior: "smooth" });
  };

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      sx={{ pt: { xs: 2, md: 3 }, pb: { xs: 6, md: "100px" }, px: { xs: 2, md: 0 } }}
    >
      <Typography
        align="center"
        sx={{
          color: "#6B7280",
          fontSize: "0.95rem",
        }}
      >
        How the homeowner likes to work and live - working style, shopping, lifestyle.
      </Typography>

      <Stack spacing={3} sx={{ px: { xs: 1.5, md: 2 } }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: "#111827", mb: 2 }}>
            Projects
          </Typography>
          {sortedProjects.length ? (
            <Box sx={{ position: "relative" }}>
              <IconButton
                aria-label="Scroll projects left"
                onClick={() => scrollProjects("left")}
                sx={{
                  position: "absolute",
                  left: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                  display: { xs: "none", md: "inline-flex" },
                  zIndex: 1,
                }}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
              <IconButton
                aria-label="Scroll projects right"
                onClick={() => scrollProjects("right")}
                sx={{
                  position: "absolute",
                  right: -12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "#fff",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.08)",
                  "&:hover": { backgroundColor: "#F9FAFB" },
                  display: { xs: "none", md: "inline-flex" },
                  zIndex: 1,
                }}
              >
                <ChevronRightRoundedIcon />
              </IconButton>
              <Box
                ref={projectScrollRef}
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  pb: 1,
                  scrollSnapType: "x mandatory",
                  scrollBehavior: "smooth",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {sortedProjects.map((project, index) => {
                  const isSelected =
                    Boolean(project._id) && project._id === selectedProjectId;
                  const startLabel = project.projectStartDate
                    ? `Start ${formatShortDate(project.projectStartDate)}`
                    : "Start date not set";
                  const endDateValue =
                    project.completionConfirmedAt ??
                    project.archivedAt ??
                    project.deadlineDate ??
                    "";
                  const endLabel = endDateValue
                    ? `End ${formatShortDate(endDateValue)}`
                    : "End date not set";
                  const budgetLabel = getProjectBudgetLabel(project);
                  return (
                    <Box
                      key={project._id ?? `${project.projectName ?? "project"}-${index}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (project._id) {
                          setSelectedProjectId(project._id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          if (project._id) {
                            setSelectedProjectId(project._id);
                          }
                        }
                      }}
                      sx={{
                        minWidth: 220,
                        borderRadius: 2,
                        border: isSelected ? "2px solid #4F46E5" : "1px solid #E5E7EB",
                        backgroundColor: isSelected ? "#EEF2FF" : "#fff",
                        boxShadow: isSelected
                          ? "0 10px 24px rgba(79, 70, 229, 0.18)"
                          : "0 6px 16px rgba(15, 23, 42, 0.06)",
                        p: 2,
                        cursor: project._id ? "pointer" : "default",
                        outline: "none",
                        scrollSnapAlign: "start",
                        "&:focus-visible": {
                          boxShadow: "0 0 0 3px rgba(79, 70, 229, 0.3)",
                        },
                      }}
                    >
                      <Stack spacing={0.6}>
                        <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                          {project.projectName || "Untitled Project"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {getProjectScopeLabel(project)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                          {startLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                          {endLabel}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6B7280" }}>
                          {`Budget ${budgetLabel}`}
                        </Typography>
                        {project.active ? (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              width: "fit-content",
                              backgroundColor: "#ECFDF3",
                              color: "#047857",
                              fontWeight: 600,
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: "#9CA3AF" }}>
              No active or completed projects yet.
            </Typography>
          )}
        </Box>

        {selectedProject ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 3fr)" },
              gap: 3,
              alignItems: "stretch",
            }}
          >
            <Box sx={{ minWidth: 0, display: "flex" }}>
              <Box
                sx={{
                  border: "1px solid #E3E7EF",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  overflow: "hidden",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Stack divider={<Divider />} spacing={0} sx={{ textAlign: "center" }}>
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography sx={{ color: "#6B7280", fontWeight: 600 }}>
                      Furniture Selection & Buying Preference:
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ mt: 2 }}
                    >
                      <Typography
                        sx={{
                          color:
                            furniturePreferenceLabel === "Not provided"
                              ? "#9CA3AF"
                              : "#4F46E5",
                          fontWeight: 600,
                        }}
                      >
                        {furniturePreferenceLabel}
                      </Typography>
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                    </Stack>
                  </Box>
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography sx={{ color: "#6B7280", fontWeight: 600 }}>
                      Decision Maker(s):
                    </Typography>
                    <Typography
                      sx={{
                        mt: 2,
                        color: decisionMakersLabel === "Not provided" ? "#9CA3AF" : "#4F46E5",
                        fontWeight: 600,
                      }}
                    >
                      {decisionMakersLabel}
                    </Typography>
                  </Box>
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography sx={{ color: "#6B7280", fontWeight: 600 }}>
                      Past Experience with Designers:
                    </Typography>
                    {workedWithDesignerValue === false ? (
                      <Typography sx={{ mt: 2, color: "#9CA3AF", fontWeight: 600 }}>
                        No
                      </Typography>
                    ) : experienceRating ? (
                      <Stack spacing={1} alignItems="center" sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1.5}>
                          {EXPERIENCE_OPTIONS.map(({ value, Icon }) => {
                            const selected = experienceRating === value;
                            return (
                              <Icon
                                key={value}
                                sx={{
                                  fontSize: 28,
                                  color: selected ? "#4F46E5" : "#D1D5DB",
                                }}
                              />
                            );
                          })}
                        </Stack>
                        <Typography sx={{ color: "#4F46E5", fontWeight: 600 }}>
                          {EXPERIENCE_LABELS[experienceRating as ExperienceRating]}
                        </Typography>
                        {experienceNotes ? (
                          <Typography
                            sx={{
                              color: "#6B7280",
                              fontStyle: "italic",
                              maxWidth: 520,
                            }}
                          >
                            "{experienceNotes}"
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography sx={{ mt: 2, color: "#9CA3AF", fontWeight: 600 }}>
                        Not provided
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography sx={{ color: "#6B7280", fontWeight: 600 }}>
                      Hobbies and Special Interests
                    </Typography>
                    {hobbiesList.length ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        {hobbiesList.map((hobby) => (
                          <Chip
                            key={hobby}
                            label={toTitleCase(hobby)}
                            size="small"
                            sx={{
                              backgroundColor: CHIP_COLOR,
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ mt: 2, color: "#9CA3AF", fontWeight: 600 }}>
                        Not provided
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: 0,
                borderLeft: { md: "1px solid #E3E7EF" },
                pl: { md: 3 },
                pt: { md: "20px" },
                display: "flex",
              }}
            >
              <Stack spacing={3} sx={{ width: "100%", height: "100%" }}>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                    Lifestyle Preferences:
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Box>
                      <Typography sx={{ color: "#6B7280" }}>Works Remotely</Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: worksRemotelyLabel === "Yes" ? "#4F46E5" : "#9CA3AF",
                          fontWeight: 600,
                        }}
                      >
                        {worksRemotelyLabel}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#6B7280" }}>
                        Entertain on a regular basis?
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.5,
                          color: entertainRegularlyLabel === "Yes" ? "#4F46E5" : "#9CA3AF",
                          fontWeight: 600,
                        }}
                      >
                        {entertainRegularlyLabel}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ color: "#6B7280" }}>Pet Type(s)</Typography>
                      {petsList.length ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                          {petsList.map((pet, index) => (
                            <Chip
                              key={`${pet.label}-${index}`}
                              icon={
                                pet.iconSrc ? (
                                  <Box
                                    component="img"
                                    src={pet.iconSrc}
                                    alt=""
                                    sx={{ width: 16, height: 16, objectFit: "contain" }}
                                  />
                                ) : undefined
                              }
                              label={pet.label}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: "#E5E7EB",
                                color: "#374151",
                                fontWeight: 600,
                              }}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={{ mt: 0.5, color: "#9CA3AF", fontWeight: 600 }}>
                          Not registered yet.
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: "#111827" }}>Household</Typography>
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {adultsLabel ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <GroupOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                        <Typography sx={{ color: "#4F46E5", fontWeight: 600 }}>
                          {adultsLabel}
                        </Typography>
                      </Stack>
                    ) : null}
                    {kidsLabel ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ChildCareOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                        <Typography sx={{ color: "#4F46E5", fontWeight: 600 }}>
                          {kidsLabel}
                          {kidsAgesLabel ? ` ${kidsAgesLabel}` : ""}
                        </Typography>
                      </Stack>
                    ) : null}
                    {!adultsLabel && !kidsLabel ? (
                      <Typography sx={{ color: "#9CA3AF", fontWeight: 600 }}>
                        Not provided
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default PreferenceTab;
